import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AtlasConfigService, AtlasDataService } from '../../../../core/src';
import type { CenterNavigationItem } from '@vendasta/protos/atlas/v1/types';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { AtlasAccountNotificationService } from '../../account-notification.service';
import { SectionContainer } from '../../vbc_sdk/_internal/objects/notifications';
import { SettingsFormComponent } from './settings-form/settings-form.component';

enum Tab {
  PERSONAL = 'Personal',
  ACCOUNT = 'Account',
}

@Component({
  selector: 'atlas-notification-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SettingsComponent implements OnInit {
  constructor(
    private config: AtlasConfigService,
    private data: AtlasDataService,
    private accountNotificationService: AtlasAccountNotificationService,
  ) {}

  static SETTINGS_DIALOG_PARAM_ID = 'open_notification_settings';
  Tab = Tab;
  @ViewChild('settingsForm') settingsForm: SettingsFormComponent;
  public partnerId$: Observable<string>;
  public userId$: Observable<string>;
  public accountGroupId$: Observable<string>;
  public redirectUrl$: Observable<string>;

  public accountsErrored = false;
  private selected: Tab;

  public showAccountsTab$: Observable<boolean> = combineLatest([
    this.config.accountId$,
    this.accountNotificationService.notificationSections$,
    this.accountNotificationService.notificationSectionsErrors$,
  ]).pipe(
    map(
      ([accountGroupId, notificationSections, notificationSectionsError]: [
        string,
        SectionContainer[],
        HttpErrorResponse | null,
      ]) => {
        const showNotificationSection =
          !!notificationSections || (notificationSectionsError && notificationSectionsError.status !== 404);
        return !!accountGroupId && showNotificationSection;
      },
    ),
  );

  public showPersonalTab$: Observable<boolean> = this.config.partnerId$.pipe(map((partnerId) => !!partnerId));

  public showEmptyState$: Observable<boolean> = combineLatest([this.showAccountsTab$, this.showPersonalTab$]).pipe(
    map(([account, personal]: [boolean, boolean]) => !account && !personal),
    startWith(false),
  );

  ngOnInit(): void {
    this.partnerId$ = this.config.partnerId$;
    this.userId$ = this.data.userId$;
    this.accountGroupId$ = this.config.accountId$;
    this.redirectUrl$ = combineLatest([this.accountGroupId$, this.userId$, this.data.centerData$]).pipe(
      map(([agid, uid, centerData]) => {
        const businessAppData = centerData.find((center: CenterNavigationItem) => center.name === 'Business App');
        if (businessAppData) {
          return (
            businessAppData.entryUrl + '&nextUrl=' + '/account/location/' + agid + '/settings/notifications/' + uid
          );
        } else {
          return null;
        }
      }),
      shareReplay(1),
    );

    combineLatest([this.showAccountsTab$, this.showPersonalTab$])
      .pipe(
        take(1),
        switchMap(([account, personal]) => {
          if (account && !personal) {
            return this.redirectUrl$;
          } else {
            return of(null);
          }
        }),
      )
      .subscribe((url) => {
        if (url) {
          window.location.href = url;
        }
      });
  }

  save(): void {
    if (this.selected === Tab.PERSONAL) {
      this.settingsForm.save();
    }
  }

  reset(): void {
    if (this.selected === Tab.PERSONAL) {
      this.settingsForm.reset();
    }
  }

  disableActions(): boolean {
    if (this.selected === Tab.PERSONAL) {
      return this.settingsForm ? !this.settingsForm.dirty() : true;
    }
    return true;
  }

  isSaving(): boolean {
    if (this.selected === Tab.PERSONAL) {
      return this.settingsForm ? this.settingsForm.saving : false;
    }
    return false;
  }

  setSelected(selected: string): void {
    this.selected = selected as Tab;
  }
}
